import type { PostHog } from "posthog-js";

export type SessionReplaySdk = Pick<
  PostHog,
  | "get_property"
  | "get_session_id"
  | "onSessionId"
  | "startSessionRecording"
  | "stopSessionRecording"
>;

export function createMerchantSessionReplay(sdk: SessionReplaySdk) {
  let userId: string | null = null;
  let unsubscribe: (() => void) | undefined;
  let recordingRequested = false;
  let recordingGeneration = 0;
  let restartPending = false;
  const recordingSessionIds = new Set<string>();

  function cancelRecordingRequest() {
    recordingRequested = false;
    recordingGeneration += 1;
    restartPending = false;
  }

  function setUser(nextUserId: string | null) {
    if (userId !== nextUserId) {
      cancelRecordingRequest();
      recordingSessionIds.clear();
      userId = nextUserId;
    }
  }

  function restartAfterSessionRotation() {
    if (restartPending) return;
    restartPending = true;
    const generation = recordingGeneration;

    // The SDK clears per-session overrides during its rotation callbacks. Wait
    // until they finish before restoring them, without re-entering the recorder.
    void Promise.resolve()
      .then(() => {
        if (generation !== recordingGeneration) return;
        restartPending = false;
        if (
          recordingRequested &&
          userId &&
          sdk.get_property("$user_id") === userId
        ) {
          sdk.startSessionRecording(true);
        }
      })
      .catch(() => {
        console.warn("Merchant session replay could not restart");
      });
  }

  function rememberSession(sessionId: string) {
    if (userId && sdk.get_property("$user_id") === userId) {
      const isNewSession = !recordingSessionIds.has(sessionId);
      recordingSessionIds.add(sessionId);
      if (isNewSession && recordingRequested) restartAfterSessionRotation();
    }
  }

  function start() {
    if (!userId || sdk.get_property("$user_id") !== userId) return;
    unsubscribe ??= sdk.onSessionId(rememberSession);
    rememberSession(sdk.get_session_id());
    recordingRequested = true;
    sdk.startSessionRecording(true);
  }

  function stop() {
    cancelRecordingRequest();
    try {
      sdk.stopSessionRecording();
    } finally {
      unsubscribe?.();
      unsubscribe = undefined;
    }
  }

  function canCaptureSnapshot(sessionId: unknown) {
    // Compression and session-ending flushes can finish after session rotation.
    // Keep sessions for this identity, but forget them before logout or a user switch.
    return (
      userId !== null &&
      sdk.get_property("$user_id") === userId &&
      typeof sessionId === "string" &&
      recordingSessionIds.has(sessionId)
    );
  }

  return { setUser, start, stop, canCaptureSnapshot };
}
